/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { databaseService } from '../database/database.service.js';
import { DexieMigrationManager } from '../database/migrations/dexie.migrations.js';

/**
 * Backup Service
 * Handles export and import of complete database as JSON
 * 
 * Features:
 * - Platform-specific backups (web/ios/android)
 * - Full database wipe before restore
 * - Version compatibility checking
 * - Metadata tracking (version, platform, timestamp)
 */
class BackupService {
  constructor() {
    this.APP_VERSION = '0.0.0'; // From package.json
  }

  /**
   * Get current database schema version
   * @private
   * @returns {Promise<number>}
   */
  async _getDatabaseVersion() {
    const platform = databaseService.getPlatform();
    
    if (platform === 'web') {
      // For Dexie, get version from adapter
      const adapter = databaseService.adapter;
      return adapter.db?.verno || adapter.DB_VERSION || 8;
    } else {
      // For SQLite, get version from migration manager
      const adapter = databaseService.adapter;
      if (adapter.migrationManager) {
        return await adapter.migrationManager.getCurrentVersion();
      }
      return 1; // Default SQLite baseline version
    }
  }

  /**
   * Export complete database to JSON format
   * @returns {Promise<Object>} Backup data with metadata
   */
  async exportBackup() {
    try {
      console.log('Starting database export...');
      
      const platform = databaseService.getPlatform();
      const schemaVersion = await this._getDatabaseVersion();
      
      // Export all tables in dependency order
      const tables = await this._exportAllTables();
      
      const backup = {
        version: schemaVersion,
        appVersion: this.APP_VERSION,
        platform: platform,
        exportDate: new Date().toISOString(),
        tables: tables
      };
      
      console.log(`Export complete: ${platform} v${schemaVersion}, ${Object.keys(tables).reduce((sum, key) => sum + tables[key].length, 0)} total records`);
      
      return backup;
    } catch (error) {
      console.error('Export failed:', error);
      throw new Error(`Failed to export database: ${error.message}`);
    }
  }

  /**
   * Export all tables from database
   * @private
   * @returns {Promise<Object>} All table data
   */
  async _exportAllTables() {
    const platform = databaseService.getPlatform();
    
    if (platform === 'web') {
      return await this._exportTablesDexie();
    } else {
      return await this._exportTablesSQLite();
    }
  }

  /**
   * Export tables using Dexie (Web platform)
   * @private
   * @returns {Promise<Object>}
   */
  async _exportTablesDexie() {
    const db = databaseService.adapter.db;
    
    return {
      groups: await db.groups.toArray(),
      goats: await db.goats.toArray(),
      weights: await db.weights.toArray(),
      temperatures: await db.temperatures.toArray(),
      famacha: await db.famacha.toArray(),
      dag: await db.dag.toArray(),
      health_records: await db.health_records.toArray()
    };
  }

  /**
   * Export tables using SQLite (Native platforms)
   * @private
   * @returns {Promise<Object>}
   */
  async _exportTablesSQLite() {
    // Query each table and convert snake_case to camelCase for consistency
    const groups = await databaseService.query('SELECT * FROM groups ORDER BY id');
    const goats = await databaseService.query('SELECT * FROM goats ORDER BY id');
    const weights = await databaseService.query('SELECT * FROM weights ORDER BY id');
    const temperatures = await databaseService.query('SELECT * FROM temperatures ORDER BY id');
    const famacha = await databaseService.query('SELECT * FROM famacha ORDER BY id');
    const dag = await databaseService.query('SELECT * FROM dag ORDER BY id');
    const healthRecords = await databaseService.query('SELECT * FROM health_records ORDER BY id');
    
    return {
      groups: this._convertToWebFormat(groups.values || [], 'groups'),
      goats: this._convertToWebFormat(goats.values || [], 'goats'),
      weights: this._convertToWebFormat(weights.values || [], 'weights'),
      temperatures: this._convertToWebFormat(temperatures.values || [], 'temperatures'),
      famacha: this._convertToWebFormat(famacha.values || [], 'famacha'),
      dag: this._convertToWebFormat(dag.values || [], 'dag'),
      health_records: this._convertToWebFormat(healthRecords.values || [], 'health_records')
    };
  }

  /**
   * Convert SQLite snake_case to Dexie camelCase format
   * @private
   * @param {Array} rows - SQLite rows
   * @param {string} tableName - Table name for specific conversions
   * @returns {Array} Converted rows
   */
  _convertToWebFormat(rows, tableName) {
    return rows.map(row => {
      const converted = {};
      
      for (const [key, value] of Object.entries(row)) {
        // Convert snake_case to camelCase
        const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
        converted[camelKey] = value;
      }
      
      // Handle table-specific conversions
      if (tableName === 'goats' && converted.notes && typeof converted.notes === 'string') {
        try {
          converted.notes = JSON.parse(converted.notes);
        } catch (e) {
          // Keep as string if not valid JSON
        }
      }
      
      return converted;
    });
  }

  /**
   * Import backup data into database (with full wipe)
   * 
   * Process:
   * 1. Validate backup structure and compatibility
   * 2. Wipe existing database completely
   * 3. Reinitialize at current app version
   * 4. Import data (preserves original format)
   * 5. Run post-import migrations if backup is older
   * 
   * Version Compatibility:
   * - Backup v10 → App v12: ✅ Migrated (v10→v11→v12)
   * - Backup v12 → App v12: ✅ No migration
   * - Backup v13 → App v12: ❌ Rejected (app too old)
   * 
   * @param {Object} backup - Backup data object with version/platform/tables
   * @returns {Promise<void>}
   */
  async importBackup(backup) {
    try {
      console.log('Starting database import...');
      
      // Validate backup structure
      this._validateBackup(backup);
      
      // Check platform compatibility
      this._checkPlatformCompatibility(backup);
      
      // Check version compatibility (reject if backup is newer than app)
      await this._checkVersionCompatibility(backup);
      
      // Wipe existing database
      console.log('Wiping existing database...');
      await databaseService.dropAllTables();
      
      // Reinitialize database
      console.log('Reinitializing database...');
      await databaseService.initialize();
      
      // Import data
      console.log('Importing data...');
      await this._importAllTables(backup.tables);
      
      // Run post-import migrations if needed
      const currentVersion = await this._getDatabaseVersion();
      const backupVersion = backup.version;
      
      if (backupVersion < currentVersion) {
        console.log('Running post-import migrations...');
        await this._runPostImportMigrations(backupVersion, currentVersion);
      }
      
      console.log('Import complete successfully');
    } catch (error) {
      console.error('Import failed:', error);
      throw error; // Re-throw to be handled by UI
    }
  }

  /**
   * Validate backup JSON structure
   * @private
   * @param {Object} backup - Backup data
   * @throws {Error} If validation fails
   */
  _validateBackup(backup) {
    if (!backup || typeof backup !== 'object') {
      throw new Error('Invalid backup file: Not a valid JSON object');
    }
    
    if (!backup.version || !backup.platform || !backup.exportDate || !backup.tables) {
      throw new Error('Invalid backup file: Missing required metadata fields (version, platform, exportDate, tables)');
    }
    
    const requiredTables = ['groups', 'goats', 'weights', 'temperatures', 'famacha', 'dag'];
    for (const table of requiredTables) {
      if (!Array.isArray(backup.tables[table])) {
        throw new Error(`Invalid backup file: Missing or invalid table data for '${table}'`);
      }
    }
  }

  /**
   * Check if backup platform matches current platform
   * @private
   * @param {Object} backup - Backup data
   * @throws {Error} If platforms don't match
   */
  _checkPlatformCompatibility(backup) {
    const currentPlatform = databaseService.getPlatform();
    const backupPlatform = backup.platform;
    
    // Allow exact match or web/native compatibility
    const isCompatible = 
      currentPlatform === backupPlatform ||
      (currentPlatform === 'web' && backupPlatform === 'web') ||
      (['ios', 'android'].includes(currentPlatform) && ['ios', 'android'].includes(backupPlatform));
    
    if (!isCompatible) {
      throw new Error(
        `Platform mismatch: Cannot restore ${backupPlatform} backup on ${currentPlatform} platform. ` +
        'Please use a backup created on the same platform type.'
      );
    }
  }

  /**
   * Check if backup version is compatible with current app
   * @private
   * @param {Object} backup - Backup data
   * @throws {Error} If version is incompatible
   */
  async _checkVersionCompatibility(backup) {
    const currentVersion = await this._getDatabaseVersion();
    const backupVersion = backup.version;
    
    if (backupVersion > currentVersion) {
      throw new Error(
        `Version mismatch: Backup is from a newer version (v${backupVersion}). ` +
        `Current app supports up to v${currentVersion}. ` +
        'Please update the app to restore this backup.'
      );
    }
    
    if (backupVersion < currentVersion) {
      console.warn(
        `Backup version (v${backupVersion}) is older than current version (v${currentVersion}). ` +
        'Data will be imported and migrations will be applied automatically.'
      );
    }
  }

  /**
   * Import all tables into database
   * @private
   * @param {Object} tables - All table data
   * @returns {Promise<void>}
   */
  async _importAllTables(tables) {
    const platform = databaseService.getPlatform();
    
    if (platform === 'web') {
      await this._importTablesDexie(tables);
    } else {
      await this._importTablesSQLite(tables);
    }
  }

  /**
   * Import tables using Dexie (Web platform)
   * @private
   * @param {Object} tables - All table data
   * @returns {Promise<void>}
   */
  async _importTablesDexie(tables) {
    const db = databaseService.adapter.db;
    
    // Deep clone to remove any Vue reactivity wrappers and ensure plain objects
    // This is necessary because IndexedDB's structured clone algorithm
    // rejects Proxy objects and other non-serializable data
    const sanitizeData = (data) => {
      return JSON.parse(JSON.stringify(data));
    };
    
    // Import in dependency order: parents before children
    // Use bulkPut() instead of bulkAdd() to preserve IDs from backup
    // bulkPut() works with auto-increment primary keys and allows specifying IDs
    await db.transaction(
      'rw',
      db.groups,
      db.goats,
      db.weights,
      db.temperatures,
      db.famacha,
      db.dag,
      db.health_records,
      async () => {
        // Import groups first (parent table)
        if (tables.groups.length > 0) {
          const cleanGroups = sanitizeData(tables.groups);
          await db.groups.bulkPut(cleanGroups);
          console.log(`Imported ${cleanGroups.length} groups`);
        }
        
        // Import goats (references groups)
        if (tables.goats.length > 0) {
          const cleanGoats = sanitizeData(tables.goats);
          await db.goats.bulkPut(cleanGoats);
          console.log(`Imported ${cleanGoats.length} goats`);
        }
        
        // Import measurements (reference goats)
        if (tables.weights.length > 0) {
          const cleanWeights = sanitizeData(tables.weights);
          await db.weights.bulkPut(cleanWeights);
          console.log(`Imported ${cleanWeights.length} weights`);
        }
        
        if (tables.temperatures.length > 0) {
          const cleanTemperatures = sanitizeData(tables.temperatures);
          await db.temperatures.bulkPut(cleanTemperatures);
          console.log(`Imported ${cleanTemperatures.length} temperatures`);
        }
        
        if (tables.famacha.length > 0) {
          const cleanFamacha = sanitizeData(tables.famacha);
          await db.famacha.bulkPut(cleanFamacha);
          console.log(`Imported ${cleanFamacha.length} famacha records`);
        }
        
        if (tables.dag.length > 0) {
          const cleanDag = sanitizeData(tables.dag);
          await db.dag.bulkPut(cleanDag);
          console.log(`Imported ${cleanDag.length} dag records`);
        }
        
        // Import health records (if available in backup)
        if (tables.health_records && tables.health_records.length > 0) {
          const cleanHealthRecords = sanitizeData(tables.health_records);
          await db.health_records.bulkPut(cleanHealthRecords);
          console.log(`Imported ${cleanHealthRecords.length} health records`);
        }
      }
    );
  }

  /**
   * Import tables using SQLite (Native platforms)
   * @private
   * @param {Object} tables - All table data
   * @returns {Promise<void>}
   */
  async _importTablesSQLite(tables) {
    // Convert camelCase back to snake_case and import
    
    // Import groups
    for (const group of tables.groups) {
      await databaseService.run(
        'INSERT INTO groups (id, name, location, description, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?)',
        [group.id, group.name, group.location, group.description, group.createdAt, group.updatedAt]
      );
    }
    console.log(`Imported ${tables.groups.length} groups`);
    
    // Import goats
    for (const goat of tables.goats) {
      await databaseService.run(
        'INSERT INTO goats (id, name, date_of_birth, place_of_birth, breed, gender, group_id, eartag, notes, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)',
        [
          goat.id,
          goat.name,
          goat.dateOfBirth,
          goat.placeOfBirth,
          goat.breed,
          goat.gender,
          goat.groupId,
          goat.eartag,
          typeof goat.notes === 'object' ? JSON.stringify(goat.notes) : goat.notes,
          goat.createdAt,
          goat.updatedAt
        ]
      );
    }
    console.log(`Imported ${tables.goats.length} goats`);
    
    // Import weights
    for (const weight of tables.weights) {
      await databaseService.run(
        'INSERT INTO weights (id, goat_id, value, measured_at, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?)',
        [weight.id, weight.goatId, weight.value, weight.measuredAt, weight.createdAt, weight.updatedAt]
      );
    }
    console.log(`Imported ${tables.weights.length} weights`);
    
    // Import temperatures
    for (const temp of tables.temperatures) {
      await databaseService.run(
        'INSERT INTO temperatures (id, goat_id, value, measured_at, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?)',
        [temp.id, temp.goatId, temp.value, temp.measuredAt, temp.createdAt, temp.updatedAt]
      );
    }
    console.log(`Imported ${tables.temperatures.length} temperatures`);
    
    // Import famacha
    for (const fam of tables.famacha) {
      await databaseService.run(
        'INSERT INTO famacha (id, goat_id, value, measured_at, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?)',
        [fam.id, fam.goatId, fam.value, fam.measuredAt, fam.createdAt, fam.updatedAt]
      );
    }
    console.log(`Imported ${tables.famacha.length} famacha records`);
    
    // Import dag
    for (const dagRecord of tables.dag) {
      await databaseService.run(
        'INSERT INTO dag (id, goat_id, value, measured_at, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?)',
        [dagRecord.id, dagRecord.goatId, dagRecord.value, dagRecord.measuredAt, dagRecord.createdAt, dagRecord.updatedAt]
      );
    }
    console.log(`Imported ${tables.dag.length} dag records`);
    
    // Import health records (if available in backup)
    if (tables.health_records) {
      for (const healthRecord of tables.health_records) {
        await databaseService.run(
          'INSERT INTO health_records (id, goat_id, type, title, description, notes, measured_at, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)',
          [
            healthRecord.id,
            healthRecord.goatId,
            healthRecord.type,
            healthRecord.title,
            healthRecord.description,
            healthRecord.notes,
            healthRecord.measuredAt,
            healthRecord.createdAt,
            healthRecord.updatedAt
          ]
        );
      }
      console.log(`Imported ${tables.health_records.length} health records`);
    }
  }

  /**
   * Run post-import migrations for older backups
   * @private
   * @param {number} fromVersion - Backup version
   * @param {number} toVersion - Current version
   * @returns {Promise<void>}
   */
  async _runPostImportMigrations(fromVersion, toVersion) {
    const platform = databaseService.getPlatform();
    
    if (platform === 'web') {
      // Run Dexie migrations
      const db = databaseService.adapter.db;
      const migrationManager = new DexieMigrationManager(db);
      
      await migrationManager.runPostImportMigrations(fromVersion, toVersion);
    } else {
      // SQLite migrations (for future implementation)
      console.log('Native platform migrations not yet implemented');
    }
  }

  /**
   * Generate backup filename
   * @returns {string} Filename with timestamp and platform
   */
  generateBackupFilename() {
    const platform = databaseService.getPlatform();
    const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    return `digiziege-backup-${platform}-${timestamp}.json`;
  }
}

// Export singleton instance
export const backupService = new BackupService();
